import React, { useEffect, useState, useCallback } from "react";
import cdrFilesService from '../../services/cdrFilesService';
import './NestingJobsPage.css'; // Добавим стили
import CdrFilesServerWarning from '../Common/CdrFilesServerWarning';

export default function NestingJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [cdrFilesServerAvailable, setCdrFilesServerAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, active, completed, error
  const [sortBy, setSortBy] = useState('creationTime'); // creationTime, name, status
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc

  const fetchJobs = useCallback(async () => {
    try {
      setError(null);
      const response = await cdrFilesService.get("/Nesting/jobs");
      setJobs(response.data);
    } catch (err) {
      console.error("Ошибка загрузки заданий", err);
      setError("Не удалось загрузить задания. Попробуйте позже.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const checkCdrFilesServerAvailable = async () => {
      try {
        await cdrFilesService.health();
        setCdrFilesServerAvailable(true);
      } catch (err) {
        console.error(err);
        setCdrFilesServerAvailable(false);
      }
    };
    checkCdrFilesServerAvailable();
  }, []);

  useEffect(() => {
    if (!cdrFilesServerAvailable) return;

    fetchJobs();
    const interval = setInterval(fetchJobs, 3000); // Уменьшим интервал для более частого обновления
    
    return () => clearInterval(interval);
  }, [cdrFilesServerAvailable, fetchJobs]);

  const abortJob = async (jobId) => {
    try {
      await cdrFilesService.post(`/Nesting/abort/${jobId}`);
      await fetchJobs();
    } catch (error) {
      console.error("Ошибка отмены задания", error);
      alert("Не удалось отменить задание. Попробуйте позже.");
    }
  };

  const toggleExpand = (jobId) => {
    setExpandedJobId(expandedJobId === jobId ? null : jobId);
  };

  const downloadFile = async (fileId, fileName) => {
    try {
      const response = await cdrFilesService.get(`/Nesting/files/${fileId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Ошибка скачивания файла", error);
      alert("Не удалось скачать файл");
    }
  };

  const statusLabels = {
    [-1]: "Ошибка",
    [0]: "Отменено",
    [1]: "Ожидание",
    [2]: "В процессе",
    [3]: "Завершено",
  };

  const statusBadgeClasses = {
    [-1]: "bg-danger",
    [0]: "bg-secondary",
    [1]: "bg-warning text-dark",
    [2]: "bg-primary",
    [3]: "bg-success",
  };

  const statusIcons = {
    [-1]: "bi-exclamation-circle",
    [0]: "bi-ban",
    [1]: "bi-clock",
    [2]: "bi-arrow-repeat",
    [3]: "bi-check-circle",
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime) return '—';
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const duration = Math.floor((end - start) / 1000); // в секундах
    
    if (duration < 60) return `${duration} сек`;
    if (duration < 3600) return `${Math.floor(duration / 60)} мин ${duration % 60} сек`;
    return `${Math.floor(duration / 3600)} ч ${Math.floor((duration % 3600) / 60)} мин`;
  };

  const getFilteredJobs = () => {
    let filtered = [...jobs];
    
    // Применяем фильтр
    switch (filter) {
      case 'active':
        filtered = filtered.filter(job => job.status === 1 || job.status === 2);
        break;
      case 'completed':
        filtered = filtered.filter(job => job.status === 3);
        break;
      case 'error':
        filtered = filtered.filter(job => job.status === -1 || job.status === 0);
        break;
      default:
        break;
    }
    
    // Применяем сортировку
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'creationTime') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  };

  const getJobStats = (job) => {
    const stats = {
      totalFiles: job.cdrFilesTotalCount || 0,
      processedFiles: job.cdrFilesProcessedCount || 0,
      errorFiles: job.cdrFilesErrorCount || 0,
      totalSheets: job.sheetsTotalCount || 0,
      processedSheets: job.sheetsProcessedCount || 0,
      progress: job.completionPercentage || 0,
      duration: formatDuration(job.startTime, job.endTime),
    };
    
    return stats;
  };

  const renderJobCard = (job) => {
    const stats = getJobStats(job);
    const isExpanded = expandedJobId === job.id;
    
    return (
      <div key={job.id} className="col-12">
        <div className={`card shadow-sm ${job.status === 2 ? 'border-primary' : ''}`}>
          <div
            className="card-body cursor-pointer"
            onClick={() => toggleExpand(job.id)}
          >
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className={`bi ${statusIcons[job.status]} ${job.status === 2 ? 'spin-animation' : ''}`}></i>
                  <h5 className="card-title mb-0">{job.name}</h5>
                  <span className={`badge ${statusBadgeClasses[job.status]}`}>
                    {statusLabels[job.status]}
                  </span>
                </div>
                
                <small className="text-muted">
                  <i className="bi bi-calendar me-1"></i>
                  Создано: {formatDate(job.creationTime)}
                </small>
                
                {job.startTime && (
                  <small className="text-muted ms-3">
                    <i className="bi bi-hourglass me-1"></i>
                    Длительность: {stats.duration}
                  </small>
                )}
                
                <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                  {job.service && (
                    <span className="badge bg-info text-dark">
                      <i className="bi bi-gear me-1"></i>
                      {job.service}
                    </span>
                  )}
                  {job.stage && (
                    <span className="badge bg-light text-dark border">
                      <i className="bi bi-arrow-repeat me-1"></i>
                      Этап: {job.stage}
                    </span>
                  )}
                  {job.error && (
                    <span className="badge bg-danger">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      Ошибка
                    </span>
                  )}
                </div>
              </div>
              
              <div className="text-end ms-3">
                <div className="mb-2">
                  <small className="text-muted d-block">Прогресс</small>
                  <strong>{stats.progress}%</strong>
                </div>
                <div className="progress" style={{ width: "150px", height: "8px" }}>
                  <div
                    className={`progress-bar ${
                      job.status === -1 ? 'bg-danger' : 
                      job.status === 0 ? 'bg-secondary' : 
                      job.status === 3 ? 'bg-success' : 
                      'progress-bar-striped progress-bar-animated'
                    }`}
                    role="progressbar"
                    style={{ width: `${stats.progress}%` }}
                    aria-valuenow={stats.progress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </div>
            </div>

            <div className="row mt-3 g-2">
              <div className="col-md-3 col-6">
                <div className="stat-box">
                  <i className="bi bi-person me-1"></i>
                  <small className="text-muted">Создатель:</small>
                  <div>{job.createdByName || 'Неизвестно'}</div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="stat-box">
                  <i className="bi bi-file-earmark me-1"></i>
                  <small className="text-muted">Файлы:</small>
                  <div>{stats.processedFiles}/{stats.totalFiles}</div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="stat-box">
                  <i className="bi bi-layers me-1"></i>
                  <small className="text-muted">Листы:</small>
                  <div>{stats.processedSheets}/{stats.totalSheets}</div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="stat-box">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  <small className="text-muted">Ошибки:</small>
                  <div className={stats.errorFiles > 0 ? 'text-danger' : 'text-success'}>
                    {stats.errorFiles}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isExpanded && (
            <>
              <div className="card-body border-top bg-light">
                <h6 className="mb-3">Детали задания</h6>
                
                {job.error && (
                  <div className="alert alert-danger py-2">
                    <strong>Ошибка:</strong> {job.error}
                  </div>
                )}
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-2">
                      <small className="text-muted">ID:</small>
                      <div className="font-monospace">{job.id}</div>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted">Сервис:</small>
                      <div>{job.service || 'Не указан'}</div>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted">Текущий этап:</small>
                      <div>{job.stage || 'Не начат'}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-2">
                      <small className="text-muted">Создан:</small>
                      <div>{formatDate(job.creationTime)}</div>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted">Начат:</small>
                      <div>{formatDate(job.startTime)}</div>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted">Завершен:</small>
                      <div>{formatDate(job.endTime)}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <small className="text-muted d-block mb-2">Прогресс обработки:</small>
                  <div className="progress mb-2" style={{ height: "25px" }}>
                    <div
                      className={`progress-bar progress-bar-striped ${
                        job.status === -1 ? 'bg-danger' : 
                        job.status === 0 ? 'bg-secondary' : 
                        job.status === 3 ? 'bg-success' : 
                        'progress-bar-animated'
                      }`}
                      role="progressbar"
                      style={{ width: `${stats.progress}%` }}
                      aria-valuenow={stats.progress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {stats.progress}%
                    </div>
                  </div>
                </div>

                {/* Секция ошибок */}
                {job.cdrFilesErrors && job.cdrFilesErrors.length > 0 && (
                  <div className="mt-4">
                    <h6 className="text-danger">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Ошибки обработки файлов ({job.cdrFilesErrors.length}):
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead>
                          <tr>
                            <th>Order ID</th>
                            <th>Файл</th>
                            <th>Ошибка</th>
                          </tr>
                        </thead>
                        <tbody>
                          {job.cdrFilesErrors.map((error, index) => (
                            <tr key={index}>
                              <td>{error.orderId}</td>
                              <td className="text-truncate" style={{ maxWidth: "200px" }}>
                                {error.cdrFilePath}
                              </td>
                              <td>
                                <span 
                                  className="text-danger" 
                                  title={error.stackTrace}
                                  style={{ cursor: 'help' }}
                                >
                                  {error.message}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Секция листов */}
                {job.nestedSheets && job.nestedSheets.length > 0 && (
                  <div className="mt-4">
                    <h6>
                      <i className="bi bi-layers me-2"></i>
                      Созданные листы ({job.nestedSheets.length}):
                    </h6>
                    <div className="row g-2">
                      {job.nestedSheets.map((sheet, index) => (
                        <div key={index} className="col-md-4 col-sm-6">
                          <div className="card">
                            <div className="card-body py-2">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <small className="text-muted">Лист {index + 1}</small>
                                  <div className="text-truncate" style={{ maxWidth: "150px" }}>
                                    <small>{sheet.cdrFilePath.split('/').pop()}</small>
                                  </div>
                                </div>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadFile(sheet.id, sheet.cdrFilePath.split('/').pop());
                                  }}
                                >
                                  <i className="bi bi-download"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Секция файлов */}
                {job.cdrFilesForNesting && job.cdrFilesForNesting.length > 0 && (
                  <div className="mt-4">
                    <h6>
                      <i className="bi bi-file-earmark me-2"></i>
                      Файлы для компоновки ({job.cdrFilesForNesting.length}):
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Order ID</th>
                            <th>Файл</th>
                          </tr>
                        </thead>
                        <tbody>
                          {job.cdrFilesForNesting.slice(0, 10).map((file, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>{file.orderId}</td>
                              <td className="text-truncate" style={{ maxWidth: "300px" }}>
                                {file.cdrFilePath}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {job.cdrFilesForNesting.length > 10 && (
                        <small className="text-muted">
                          Показаны первые 10 из {job.cdrFilesForNesting.length} файлов
                        </small>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="card-footer d-flex flex-wrap gap-2">
                {(job.status === 1 || job.status === 2) && (
                  <button
                    className="btn btn-warning"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Вы уверены, что хотите отменить это задание?')) {
                        abortJob(job.id);
                      }
                    }}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Отменить задание
                  </button>
                )}
                {job.status === 3 && (
                  <span className="text-success">
                    <i className="bi bi-check-circle me-1"></i>
                    Задание успешно завершено
                  </span>
                )}
                {job.status === -1 && (
                  <span className="text-danger">
                    <i className="bi bi-exclamation-circle me-1"></i>
                    Задание завершилось с ошибкой
                  </span>
                )}
                {job.status === 0 && (
                  <span className="text-secondary">
                    <i className="bi bi-ban me-1"></i>
                    Задание отменено пользователем
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const filteredJobs = getFilteredJobs();

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between mb-4 flex-wrap gap-3">
        <h1 className="h3">Задания на компоновку</h1>
        <div className="d-flex align-items-center gap-2">
          <span className={`badge ${cdrFilesServerAvailable ? 'bg-success' : 'bg-danger'} me-2`}>
            <i className={`bi ${cdrFilesServerAvailable ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
            {cdrFilesServerAvailable ? 'Сервер доступен' : 'Сервер недоступен'}
          </span>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={fetchJobs}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Обновить
          </button>
        </div>
      </div>

      {/* Панель фильтров */}
      <div className="mb-4">
        <div className="d-flex gap-2 flex-wrap">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('all')}
            >
              Все
            </button>
            <button
              type="button"
              className={`btn btn-sm ${filter === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('active')}
            >
              Активные
            </button>
            <button
              type="button"
              className={`btn btn-sm ${filter === 'completed' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('completed')}
            >
              Завершенные
            </button>
            <button
              type="button"
              className={`btn btn-sm ${filter === 'error' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('error')}
            >
              С ошибками
            </button>
          </div>
          
          <div className="btn-group" role="group">
            <select 
              className="form-select form-select-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="creationTime">По дате создания</option>
              <option value="name">По имени</option>
              <option value="status">По статусу</option>
            </select>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              <i className={`bi ${sortOrder === 'asc' ? 'bi-sort-up' : 'bi-sort-down'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <CdrFilesServerWarning cdrFilesServerAvailable={cdrFilesServerAvailable} />
      
      {loading && cdrFilesServerAvailable && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Загрузка...</span>
          </div>
        </div>
      )}

      {!loading && filteredJobs.length === 0 && cdrFilesServerAvailable && (
        <div className="text-center text-muted py-5">
          <i className="bi bi-inbox display-1"></i>
          <h4>Нет заданий на компоновку</h4>
          <p>
            {filter === 'all' 
              ? 'Задания появятся здесь, когда будут созданы' 
              : 'Нет заданий, соответствующих выбранному фильтру'}
          </p>
        </div>
      )}

      <div className="row g-4">
        {filteredJobs.map(renderJobCard)}
      </div>
    </div>
  );
}